import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Tag, FileText, MapPin, Printer, ImagePlus, Mic, Calendar,
  DollarSign, Image, CheckCircle2, BarChart3, ChevronRight, Eye, Edit3,
  BookOpen, RefreshCw, Search, X, ChevronDown, ChevronUp, ArrowUpDown, Plus, Star,
  GripVertical, Save
} from 'lucide-react';
import { supabase } from '../utils/supabaseClient';
import '../styles/EnrichmentDrilldown.css';

// Category definitions matching the scorecard
const CATEGORIES = [
  { key: 'title', label: 'Title', icon: FileText, color: '#10b981', type: 'field' },
  { key: 'status', label: 'Status', icon: CheckCircle2, color: '#10b981', type: 'field' },
  { key: 'price', label: 'Price', icon: DollarSign, color: '#10b981', type: 'field' },
  { key: 'dimensions', label: 'Dimensions', icon: Image, color: '#6366f1', type: 'field' },
  { key: 'completionDate', label: 'Completion Date', icon: Calendar, color: '#6366f1', type: 'field' },
  { key: 'location', label: 'Location', icon: MapPin, color: '#8b5cf6', type: 'lookup', table: 'galleries' },
  { key: 'tags', label: 'Tags', icon: Tag, color: '#8b5cf6', type: 'tags' },
  { key: 'prints', label: 'Prints', icon: Printer, color: '#f59e0b', type: 'lookup', table: 'products' },
  { key: 'story', label: 'Story', icon: FileText, color: '#f59e0b', type: 'story' },
  { key: 'additionalImages', label: "Add'l Images", icon: ImagePlus, color: '#ef4444', type: 'media', mediaType: 'Image' },
  { key: 'audio', label: 'Audio Story', icon: Mic, color: '#ef4444', type: 'media', mediaType: 'Audio' },
];

const PAGE_SIZE = 1000;

// Helper: paginate through all rows
async function fetchAllRows(buildQuery) {
  let allRows = [];
  let from = 0;
  while (true) {
    const { data, error } = await buildQuery().range(from, from + PAGE_SIZE - 1);
    if (error) throw error;
    allRows = allRows.concat(data || []);
    if (!data || data.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }
  return allRows;
}

// Helper: chunked .in() queries
async function fetchInChunks(table, selectCols, filterCol, ids, extraFilters) {
  const CHUNK = 500;
  let allRows = [];
  for (let i = 0; i < ids.length; i += CHUNK) {
    const chunk = ids.slice(i, i + CHUNK);
    let q = supabase.from(table).select(selectCols).in(filterCol, chunk);
    if (extraFilters) q = extraFilters(q);
    let from = 0;
    while (true) {
      const { data, error } = await q.range(from, from + 999);
      if (error) throw error;
      allRows = allRows.concat(data || []);
      if (!data || data.length < 1000) break;
      from += 1000;
      q = supabase.from(table).select(selectCols).in(filterCol, chunk);
      if (extraFilters) q = extraFilters(q);
    }
  }
  return allRows;
}

function getDisplayImageUrl(url) {
  if (!url) return null;
  if (url.includes('cloudinary.com') && url.includes('/upload/')) {
    return url.replace('/upload/', '/upload/t_fitmax-300/');
  }
  return url;
}

const EnrichmentDrilldown = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get('category') || 'tags';
  const activeSubFilter = searchParams.get('filter') || null; // e.g., a specific tag name
  const showMissing = searchParams.get('show') === 'missing';
  const initialCollectionId = searchParams.get('collection') || '';

  const [loading, setLoading] = useState(true);
  const [categoryData, setCategoryData] = useState(null); // breakdown items (e.g., each tag)
  const [artworks, setArtworks] = useState([]);
  const [allArtworkIds, setAllArtworkIds] = useState([]);
  const [artworkSearch, setArtworkSearch] = useState('');
  const [collectionFilter, setCollectionFilter] = useState(initialCollectionId);
  const [collectionSearch, setCollectionSearch] = useState('');
  const [showCollectionDropdown, setShowCollectionDropdown] = useState(false);
  const [collections, setCollections] = useState([]); // [{collection_id, title, artwork_count}]
  const [artworkCollections, setArtworkCollections] = useState({}); // vc_id -> [collection_id, ...]
  const [collectionAccepted, setCollectionAccepted] = useState({}); // `${vc_id}_${collection_id}` -> boolean
  const [acceptedOnly, setAcceptedOnly] = useState(false);
  const collectionRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(24);
  const loadMoreRef = useRef(null);
  const [dimensionMode, setDimensionMode] = useState('overall'); // overall | width | height
  const [imageViewMode, setImageViewMode] = useState('byCategory'); // byCategory | byCount
  const [sortBy, setSortBy] = useState('newest'); // newest|oldest|title-asc|title-desc|price-high|price-low|artist-asc
  const ARTWORK_PAGE_SIZE = 24;

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisibleCount(prev => prev + ARTWORK_PAGE_SIZE);
        }
      },
      { threshold: 0.1 }
    );
    const el = loadMoreRef.current;
    if (el) observer.observe(el);
    return () => { if (el) observer.unobserve(el); };
  }, [loading, categoryData]);

  // Tag editor state
  const [tagEditorArtwork, setTagEditorArtwork] = useState(null);
  const [availableTags, setAvailableTags] = useState([]);
  const [artworkTagMappings, setArtworkTagMappings] = useState([]); // [{mappingId, attributeId}]
  const [loadingTagEditor, setLoadingTagEditor] = useState(false);
  const [togglingTagId, setTogglingTagId] = useState(null);
  const [reviewStatus, setReviewStatus] = useState(null); // { reviewedBy, reviewedAt, reviewerName }
  const [markingReviewed, setMarkingReviewed] = useState(false);
  const [addingTagType, setAddingTagType] = useState(null); // which type section has the input open
  const [newTagTitle, setNewTagTitle] = useState('');
  const [savingNewTag, setSavingNewTag] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [tagCoverImages, setTagCoverImages] = useState({}); // { attributeId: { vc_id, cover_image, title } }
  const [settingCover, setSettingCover] = useState(null); // attributeId currently being set
  const [reorderingType, setReorderingType] = useState(null); // which tag type is being reordered
  const [reorderList, setReorderList] = useState([]); // temp ordered list for drag
  const [savingOrder, setSavingOrder] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const tagEditorRef = useRef(null);

  // Get current user on mount
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (data?.user?.id) setCurrentUserId(data.user.id);
    });
  }, []);

  const categoryDef = CATEGORIES.find(c => c.key === activeCategory) || CATEGORIES[6]; // default to tags
  const CategoryIcon = categoryDef.icon;

  // Set the active category
  const setCategory = useCallback((key) => {
    setSearchParams({ category: key });
    setVisibleCount(24);
    setArtworkSearch('');
    setCollectionFilter('');
    setCollectionSearch('');
    setAcceptedOnly(false);
    setDimensionMode('overall');
    setImageViewMode('byCategory');
    setSortBy('newest');
  }, [setSearchParams]);

  // Toggle has/missing view
  const toggleShowMissing = useCallback(() => {
    const params = new URLSearchParams(searchParams);
    if (showMissing) {
      params.delete('show');
    } else {
      params.set('show', 'missing');
    }
    params.delete('filter');
    setSearchParams(params);
    setVisibleCount(24);
  }, [searchParams, showMissing, setSearchParams]);

  // Set sub-filter (e.g., a specific tag)
  const setSubFilter = useCallback((filter) => {
    const params = new URLSearchParams(searchParams);
    if (filter) {
      params.set('filter', filter);
      params.delete('show');
    } else {
      params.delete('filter');
    }
    setSearchParams(params);
    setVisibleCount(24);
  }, [searchParams, setSearchParams]);

  // Fetch data for the active category
  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      setLoading(true);
      setCategoryData(null);
      setArtworks([]);

      try {
        // Step 1: Get all public artwork IDs
        const allArts = await fetchAllRows(() =>
          supabase
            .from('vcs')
            .select('vc_id, title, price, status, location, artwork_completed_date, width, height, cover_image, uuid, description, tags_reviewed_by, tags_reviewed_at, more')
            .eq('access_control', 'Public')
            .ilike('cover_image', '%res.cloudinary.com/dh9jfogh1%')
        );
        if (cancelled) return;

        // Build artist lookup
        const userIds = [...new Set(allArts.map(a => a.uuid).filter(Boolean))];
        const userRows = await fetchInChunks('users', 'uuid, first_name, last_name', 'uuid', userIds);
        const userMap = {};
        userRows.forEach(u => { userMap[u.uuid] = `${u.first_name || ''} ${u.last_name || ''}`.trim(); });

        const artMap = {};
        allArts.forEach(a => {
          artMap[a.vc_id] = { ...a, artist_name: userMap[a.uuid] || 'Unknown' };
        });
        const vcIds = allArts.map(a => a.vc_id);
        setAllArtworkIds(vcIds);

        // Load collection mappings for all artworks
        const collMappings = await fetchInChunks(
          'collection_vc_mapping', 'vc_id, collection_id, accepted', 'vc_id', vcIds
        );
        const vcCollMap = {};
        const collIds = new Set();
        const acceptedMap = {};
        collMappings.forEach(m => {
          if (!vcCollMap[m.vc_id]) vcCollMap[m.vc_id] = [];
          vcCollMap[m.vc_id].push(m.collection_id);
          collIds.add(m.collection_id);
          acceptedMap[`${m.vc_id}_${m.collection_id}`] = !!m.accepted;
        });
        setArtworkCollections(vcCollMap);
        setCollectionAccepted(acceptedMap);

        // Load collection names
        if (collIds.size > 0) {
          const collRows = await fetchInChunks(
            'collections', 'collection_id, title, type', 'collection_id', [...collIds]
          );
          const collCounts = {};
          collMappings.forEach(m => {
            collCounts[m.collection_id] = (collCounts[m.collection_id] || 0) + 1;
          });
          const sorted = collRows
            .filter(c => c.type !== 'portfolio' && c.type !== 'profile_order')
            .map(c => ({ ...c, artwork_count: collCounts[c.collection_id] || 0 }))
            .sort((a, b) => (a.title || '').localeCompare(b.title || ''));
          setCollections(sorted);
        }

        // Step 2: load category-specific data
        if (categoryDef.key === 'tags') {
          await loadTagsData(vcIds, artMap, cancelled);
        } else if (categoryDef.key === 'status') {
          loadFieldBreakdown(allArts, artMap, 'status');
        } else if (categoryDef.key === 'location') {
          await loadLocationData(vcIds, allArts, artMap, cancelled);
        } else if (categoryDef.key === 'additionalImages') {
          await loadAdditionalImagesData(vcIds, artMap, cancelled);
        } else if (categoryDef.key === 'story') {
          loadStoryData(allArts, artMap);
        } else if (categoryDef.type === 'media') {
          await loadMediaData(vcIds, artMap, categoryDef.mediaType, cancelled);
        } else if (categoryDef.key === 'prints') {
          await loadPrintsData(vcIds, artMap, cancelled);
        } else if (categoryDef.key === 'price') {
          loadPriceRangeData(allArts, artMap);
        } else if (categoryDef.key === 'dimensions') {
          loadDimensionsRangeData(allArts, artMap);
        } else {
          // Simple field categories (title, completionDate)
          loadSimpleFieldData(allArts, artMap, categoryDef.key);
        }
      } catch (error) {
        console.error('Error loading enrichment drilldown:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadData();
    return () => { cancelled = true; };
  }, [activeCategory]);

  // === DATA LOADERS ===

  const loadTagsData = async (vcIds, artMap, cancelled) => {
    // Get all tag assignments with tag details
    const tagRows = await fetchInChunks(
      'attribute_vc_mapping',
      'vc_id, artwork_attributes!inner(id, title, type)',
      'vc_id',
      vcIds
    );
    if (cancelled) return;

    // Group by tag type, then by tag title
    const tagGroups = {}; // { 'style': { 'Abstract': { count, vcIds }, ... }, ... }
    const artworksWithTags = new Set();

    tagRows.forEach(row => {
      const attr = row.artwork_attributes;
      if (!attr) return;
      const type = attr.type || 'other';
      const title = attr.title || 'Untitled';

      if (!tagGroups[type]) tagGroups[type] = {};
      if (!tagGroups[type][title]) tagGroups[type][title] = { count: 0, vcIds: new Set() };
      tagGroups[type][title].count++;
      tagGroups[type][title].vcIds.add(row.vc_id);
      artworksWithTags.add(row.vc_id);
    });

    // Convert to sorted arrays
    const breakdown = {};
    Object.entries(tagGroups).forEach(([type, tags]) => {
      breakdown[type] = Object.entries(tags)
        .map(([title, data]) => ({ title, count: data.count, vcIds: [...data.vcIds] }))
        .sort((a, b) => b.count - a.count);
    });

    const missingIds = vcIds.filter(id => !artworksWithTags.has(id));
    const curatedCount = vcIds.filter(id => artMap[id]?.tags_reviewed_at).length;

    setCategoryData({
      type: 'tags',
      breakdown,
      totalWith: artworksWithTags.size,
      totalWithout: missingIds.length,
      totalCurated: curatedCount,
      missingIds,
      artMap,
    });
  };

  const loadFieldBreakdown = (allArts, artMap, field) => {
    const groups = {};
    const missingIds = [];

    allArts.forEach(a => {
      const val = a[field];
      if (val && String(val).trim()) {
        const key = String(val).trim();
        if (!groups[key]) groups[key] = { count: 0, vcIds: [] };
        groups[key].count++;
        groups[key].vcIds.push(a.vc_id);
      } else {
        missingIds.push(a.vc_id);
      }
    });

    const items = Object.entries(groups)
      .map(([title, data]) => ({ title, count: data.count, vcIds: data.vcIds }))
      .sort((a, b) => b.count - a.count);

    setCategoryData({
      type: 'breakdown',
      items,
      totalWith: allArts.length - missingIds.length,
      totalWithout: missingIds.length,
      missingIds,
      artMap,
    });
  };

  const loadLocationData = async (vcIds, allArts, artMap, cancelled) => {
    // Get all galleries referenced by artworks
    const artworksWithLocation = allArts.filter(a => a.location);
    const galleryIds = [...new Set(artworksWithLocation.map(a => a.location))];

    let galleryMap = {};
    if (galleryIds.length > 0) {
      const galleryRows = await fetchInChunks('galleries', 'gallery_id, name', 'gallery_id', galleryIds);
      if (cancelled) return;
      galleryRows.forEach(g => { galleryMap[g.gallery_id] = g.name; });
    }

    // Group by gallery
    const groups = {};
    const missingIds = [];

    allArts.forEach(a => {
      if (a.location && galleryMap[a.location]) {
        const name = galleryMap[a.location];
        if (!groups[name]) groups[name] = { count: 0, vcIds: [] };
        groups[name].count++;
        groups[name].vcIds.push(a.vc_id);
      } else if (!a.location) {
        missingIds.push(a.vc_id);
      }
    });

    const items = Object.entries(groups)
      .map(([title, data]) => ({ title, count: data.count, vcIds: data.vcIds }))
      .sort((a, b) => b.count - a.count);

    setCategoryData({
      type: 'breakdown',
      items,
      totalWith: allArts.length - missingIds.length,
      totalWithout: missingIds.length,
      missingIds,
      artMap,
    });
  };

  const IMAGE_CATEGORIES = ['On-Wall', 'Framed', 'Close-Up', 'Detail', 'Signed', 'Unframed', 'In-Progress', 'Other'];

  const loadAdditionalImagesData = async (vcIds, artMap, cancelled) => {
    const mediaRows = await fetchInChunks(
      'media_elements',
      'vc_id, type, title',
      'vc_id',
      vcIds,
      q => q.eq('type', 'Image')
    );
    if (cancelled) return;

    const withMedia = new Set();
    const countByArtwork = {};
    const categoryGroups = {}; // { 'On-Wall': { count, vcIds }, ... }

    mediaRows.forEach(m => {
      withMedia.add(m.vc_id);
      countByArtwork[m.vc_id] = (countByArtwork[m.vc_id] || 0) + 1;

      // Group by category (title)
      const cat = m.title || 'Other';
      if (!categoryGroups[cat]) categoryGroups[cat] = { count: 0, vcIds: new Set() };
      categoryGroups[cat].count++;
      categoryGroups[cat].vcIds.add(m.vc_id);
    });

    const missingIds = vcIds.filter(id => !withMedia.has(id));

    // Count-based breakdown
    const countGroups = {};
    Object.entries(countByArtwork).forEach(([vcId, count]) => {
      const label = `${count} image${count > 1 ? 's' : ''}`;
      if (!countGroups[label]) countGroups[label] = { count: 0, vcIds: [], sortKey: count };
      countGroups[label].count++;
      countGroups[label].vcIds.push(parseInt(vcId));
    });

    const byCount = Object.entries(countGroups)
      .map(([title, data]) => ({ title, count: data.count, vcIds: data.vcIds, sortKey: data.sortKey }))
      .sort((a, b) => b.sortKey - a.sortKey);

    // Category-based breakdown (ordered by IMAGE_CATEGORIES, then any extras)
    const byCategory = IMAGE_CATEGORIES
      .filter(cat => categoryGroups[cat])
      .map(cat => ({
        title: cat,
        count: categoryGroups[cat].count,
        vcIds: [...categoryGroups[cat].vcIds],
      }));
    // Add any categories not in the known list
    Object.keys(categoryGroups).forEach(cat => {
      if (!IMAGE_CATEGORIES.includes(cat)) {
        byCategory.push({
          title: cat,
          count: categoryGroups[cat].count,
          vcIds: [...categoryGroups[cat].vcIds],
        });
      }
    });

    setCategoryData({
      type: 'imageMedia',
      byCount,
      byCategory,
      totalWith: withMedia.size,
      totalWithout: missingIds.length,
      missingIds,
      artMap,
    });
  };

  const loadMediaData = async (vcIds, artMap, mediaType, cancelled) => {
    const mediaRows = await fetchInChunks(
      'media_elements',
      'vc_id, type, title',
      'vc_id',
      vcIds,
      q => q.eq('type', mediaType)
    );
    if (cancelled) return;

    const withMedia = new Set();
    const countByArtwork = {};
    mediaRows.forEach(m => {
      withMedia.add(m.vc_id);
      countByArtwork[m.vc_id] = (countByArtwork[m.vc_id] || 0) + 1;
    });

    const missingIds = vcIds.filter(id => !withMedia.has(id));

    // Create a breakdown by count
    const countGroups = {};
    Object.entries(countByArtwork).forEach(([vcId, count]) => {
      const label = `${count} ${mediaType.toLowerCase()}${count > 1 ? 's' : ''}`;
      if (!countGroups[label]) countGroups[label] = { count: 0, vcIds: [], sortKey: count };
      countGroups[label].count++;
      countGroups[label].vcIds.push(parseInt(vcId));
    });

    const items = Object.entries(countGroups)
      .map(([title, data]) => ({ title, count: data.count, vcIds: data.vcIds, sortKey: data.sortKey }))
      .sort((a, b) => b.sortKey - a.sortKey);

    setCategoryData({
      type: 'breakdown',
      items,
      totalWith: withMedia.size,
      totalWithout: missingIds.length,
      missingIds,
      artMap,
    });
  };

  const loadStoryData = (allArts, artMap) => {
    const hasProcess = [];
    const hasInspiration = [];
    const hasBoth = [];
    const missingIds = [];

    allArts.forEach(a => {
      const more = a.more;
      const hasP = more?.process && more.process.trim().length > 0;
      const hasI = more?.inspiration && more.inspiration.trim().length > 0;
      if (hasP && hasI) {
        hasBoth.push(a.vc_id);
      } else if (hasP) {
        hasProcess.push(a.vc_id);
      } else if (hasI) {
        hasInspiration.push(a.vc_id);
      } else {
        missingIds.push(a.vc_id);
      }
    });

    const items = [];
    if (hasBoth.length > 0) items.push({ title: 'Process & Inspiration', count: hasBoth.length, vcIds: hasBoth });
    if (hasInspiration.length > 0) items.push({ title: 'Inspiration Only', count: hasInspiration.length, vcIds: hasInspiration });
    if (hasProcess.length > 0) items.push({ title: 'Process Only', count: hasProcess.length, vcIds: hasProcess });

    const totalWith = hasBoth.length + hasProcess.length + hasInspiration.length;
    setCategoryData({
      type: 'breakdown',
      items,
      totalWith,
      totalWithout: missingIds.length,
      missingIds,
      artMap,
    });
  };

  const loadPrintsData = async (vcIds, artMap, cancelled) => {
    const printRows = await fetchInChunks(
      'products',
      'vc_id, title, price',
      'vc_id',
      vcIds,
      q => q.eq('type', 'print')
    );
    if (cancelled) return;

    const withPrints = new Set();
    const countByArtwork = {};
    printRows.forEach(p => {
      withPrints.add(p.vc_id);
      countByArtwork[p.vc_id] = (countByArtwork[p.vc_id] || 0) + 1;
    });

    const missingIds = vcIds.filter(id => !withPrints.has(id));

    const countGroups = {};
    Object.entries(countByArtwork).forEach(([vcId, count]) => {
      const label = `${count} print${count > 1 ? 's' : ''} configured`;
      if (!countGroups[label]) countGroups[label] = { count: 0, vcIds: [], sortKey: count };
      countGroups[label].count++;
      countGroups[label].vcIds.push(parseInt(vcId));
    });

    const items = Object.entries(countGroups)
      .map(([title, data]) => ({ title, count: data.count, vcIds: data.vcIds, sortKey: data.sortKey }))
      .sort((a, b) => b.sortKey - a.sortKey);

    setCategoryData({
      type: 'breakdown',
      items,
      totalWith: withPrints.size,
      totalWithout: missingIds.length,
      missingIds,
      artMap,
    });
  };

  const loadSimpleFieldData = (allArts, artMap, fieldKey) => {
    const fieldChecks = {
      title: a => a.title && a.title.trim(),
      completionDate: a => a.artwork_completed_date,
    };

    const check = fieldChecks[fieldKey] || (() => false);
    const hasIds = [];
    const missingIds = [];

    allArts.forEach(a => {
      if (check(a)) {
        hasIds.push(a.vc_id);
      } else {
        missingIds.push(a.vc_id);
      }
    });

    setCategoryData({
      type: 'simple',
      hasIds,
      totalWith: hasIds.length,
      totalWithout: missingIds.length,
      missingIds,
      artMap,
    });
  };

  const PRICE_RANGES = [
    { label: 'Under $250', min: 0.01, max: 250 },
    { label: '$250 – $500', min: 250.01, max: 500 },
    { label: '$500 – $1K', min: 500.01, max: 1000 },
    { label: '$1K – $2.5K', min: 1000.01, max: 2500 },
    { label: '$2.5K – $5K', min: 2500.01, max: 5000 },
    { label: 'Over $5K', min: 5000.01, max: Infinity },
  ];

  const loadPriceRangeData = (allArts, artMap) => {
    const missingIds = [];
    const buckets = PRICE_RANGES.map(r => ({ ...r, vcIds: [] }));

    allArts.forEach(a => {
      const p = a.price;
      if (!p || p <= 0) {
        missingIds.push(a.vc_id);
        return;
      }
      for (const bucket of buckets) {
        if (p >= bucket.min && p <= bucket.max) {
          bucket.vcIds.push(a.vc_id);
          break;
        }
      }
    });

    const items = buckets.map(b => ({
      title: b.label,
      count: b.vcIds.length,
      vcIds: b.vcIds,
    }));

    setCategoryData({
      type: 'breakdown',
      items,
      totalWith: allArts.length - missingIds.length,
      totalWithout: missingIds.length,
      missingIds,
      artMap,
    });
  };

  const DIM_RANGES = [
    { label: 'Small (< 24")', min: 0.01, max: 23.99 },
    { label: 'Medium (24–48")', min: 24, max: 48 },
    { label: 'Large (> 48")', min: 48.01, max: Infinity },
  ];

  const loadDimensionsRangeData = (allArts, artMap) => {
    const missingIds = [];
    const hasDimIds = [];

    // Build per-mode breakdowns
    const makeBuckets = () => DIM_RANGES.map(r => ({ ...r, vcIds: [] }));
    const overallBuckets = makeBuckets();
    const widthBuckets = makeBuckets();
    const heightBuckets = makeBuckets();

    allArts.forEach(a => {
      const w = a.width;
      const h = a.height;
      if (!w && !h) {
        missingIds.push(a.vc_id);
        return;
      }
      hasDimIds.push(a.vc_id);

      // Overall = largest dimension
      const maxDim = Math.max(w || 0, h || 0);
      for (const bucket of overallBuckets) {
        if (maxDim >= bucket.min && maxDim <= bucket.max) {
          bucket.vcIds.push(a.vc_id);
          break;
        }
      }
      // Width
      if (w && w > 0) {
        for (const bucket of widthBuckets) {
          if (w >= bucket.min && w <= bucket.max) {
            bucket.vcIds.push(a.vc_id);
            break;
          }
        }
      }
      // Height
      if (h && h > 0) {
        for (const bucket of heightBuckets) {
          if (h >= bucket.min && h <= bucket.max) {
            bucket.vcIds.push(a.vc_id);
            break;
          }
        }
      }
    });

    const toItems = (buckets) => buckets.map(b => ({
      title: b.label,
      count: b.vcIds.length,
      vcIds: b.vcIds,
    }));

    setCategoryData({
      type: 'dimensions',
      overall: toItems(overallBuckets),
      byWidth: toItems(widthBuckets),
      byHeight: toItems(heightBuckets),
      totalWith: hasDimIds.length,
      totalWithout: missingIds.length,
      missingIds,
      artMap,
    });
  };

  // === ARTWORK LIST FOR CURRENT VIEW ===

  const getDisplayArtworks = useCallback(() => {
    if (!categoryData) return [];

    let ids = [];

    if (activeSubFilter && categoryData.type === 'tags') {
      // Find artworks for a specific tag
      for (const tags of Object.values(categoryData.breakdown || {})) {
        const match = tags.find(t => t.title === activeSubFilter);
        if (match) { ids = match.vcIds; break; }
      }
    } else if (activeSubFilter && categoryData.type === 'breakdown') {
      const match = categoryData.items?.find(i => i.title === activeSubFilter);
      if (match) ids = match.vcIds;
    } else if (activeSubFilter && categoryData.type === 'imageMedia') {
      const items = imageViewMode === 'byCount' ? categoryData.byCount : categoryData.byCategory;
      const match = items?.find(i => i.title === activeSubFilter);
      if (match) ids = match.vcIds;
    } else if (activeSubFilter && categoryData.type === 'dimensions') {
      const items = dimensionMode === 'width' ? categoryData.byWidth
        : dimensionMode === 'height' ? categoryData.byHeight
        : categoryData.overall;
      const match = items?.find(i => i.title === activeSubFilter);
      if (match) ids = match.vcIds;
    } else if (showMissing) {
      ids = categoryData.missingIds || [];
    } else if (categoryData.type === 'simple') {
      ids = categoryData.hasIds || [];
    } else if (categoryData.type === 'imageMedia') {
      const items = imageViewMode === 'byCount' ? categoryData.byCount : categoryData.byCategory;
      const allIds = new Set();
      (items || []).forEach(item => item.vcIds.forEach(id => allIds.add(id)));
      ids = [...allIds];
    } else if (categoryData.type === 'dimensions') {
      const items = dimensionMode === 'width' ? categoryData.byWidth
        : dimensionMode === 'height' ? categoryData.byHeight
        : categoryData.overall;
      const allIds = new Set();
      (items || []).forEach(item => item.vcIds.forEach(id => allIds.add(id)));
      ids = [...allIds];
    } else if (categoryData.type === 'tags') {
      // All artworks with tags
      const allIds = new Set();
      Object.values(categoryData.breakdown || {}).forEach(tags => {
        tags.forEach(t => t.vcIds.forEach(id => allIds.add(id)));
      });
      ids = [...allIds];
    } else {
      // All artworks with this field
      const allIds = new Set();
      (categoryData.items || []).forEach(item => item.vcIds.forEach(id => allIds.add(id)));
      ids = [...allIds];
    }

    // Apply search filter
    let filtered = ids.map(id => categoryData.artMap[id]).filter(Boolean);
    if (collectionFilter) {
      const colId = parseInt(collectionFilter, 10);
      filtered = filtered.filter(a => {
        if (!artworkCollections[a.vc_id]?.includes(colId)) return false;
        if (acceptedOnly && !collectionAccepted[`${a.vc_id}_${colId}`]) return false;
        return true;
      });
    }
    if (artworkSearch.trim()) {
      const q = artworkSearch.toLowerCase();
      filtered = filtered.filter(a =>
        (a.title || '').toLowerCase().includes(q) ||
        (a.artist_name || '').toLowerCase().includes(q)
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'oldest':
          return (a.vc_id || 0) - (b.vc_id || 0);
        case 'title-asc':
          return (a.title || '').localeCompare(b.title || '');
        case 'title-desc':
          return (b.title || '').localeCompare(a.title || '');
        case 'price-high':
          return (b.price ?? -1) - (a.price ?? -1);
        case 'price-low':
          return (a.price ?? Infinity) - (b.price ?? Infinity);
        case 'artist-asc':
          return (a.artist_name || '').localeCompare(b.artist_name || '');
        case 'newest':
        default:
          return (b.vc_id || 0) - (a.vc_id || 0);
      }
    });

    return filtered;
  }, [categoryData, activeSubFilter, showMissing, artworkSearch, collectionFilter, artworkCollections, collectionAccepted, acceptedOnly, dimensionMode, imageViewMode, sortBy]);

  const displayArtworks = getDisplayArtworks();
  const visibleArtworks = displayArtworks.slice(0, visibleCount);

  // Recompute left-panel category data when collection/accepted filter is active
  const filteredCategoryData = useMemo(() => {
    if (!categoryData) return null;
    if (!collectionFilter) return categoryData;

    const colId = parseInt(collectionFilter, 10);
    const isInFilter = (vcId) => {
      if (!artworkCollections[vcId]?.includes(colId)) return false;
      if (acceptedOnly && !collectionAccepted[`${vcId}_${colId}`]) return false;
      return true;
    };

    if (categoryData.type === 'tags') {
      const newBreakdown = {};
      const artworksWithTags = new Set();
      for (const [type, tags] of Object.entries(categoryData.breakdown || {})) {
        newBreakdown[type] = tags.map(tag => {
          const filteredIds = tag.vcIds.filter(isInFilter);
          filteredIds.forEach(id => artworksWithTags.add(id));
          return { ...tag, vcIds: filteredIds, count: filteredIds.length };
        }).filter(tag => tag.count > 0);
        if (newBreakdown[type].length === 0) delete newBreakdown[type];
      }
      const allFilteredIds = Object.keys(categoryData.artMap).map(Number).filter(isInFilter);
      const missingIds = allFilteredIds.filter(id => !artworksWithTags.has(id));
      const curatedCount = allFilteredIds.filter(id => categoryData.artMap[id]?.tags_reviewed_at).length;
      return {
        ...categoryData,
        breakdown: newBreakdown,
        totalWith: artworksWithTags.size,
        totalWithout: missingIds.length,
        totalCurated: curatedCount,
        missingIds,
      };
    }

    if (categoryData.type === 'dimensions') {
      const filterArray = (arr) => (arr || []).map(item => {
        const filteredIds = item.vcIds.filter(isInFilter);
        return { ...item, vcIds: filteredIds, count: filteredIds.length };
      }).filter(item => item.count > 0);
      const allFilteredIds = Object.keys(categoryData.artMap).map(Number).filter(isInFilter);
      const withIds = new Set();
      (categoryData.overall || []).forEach(item => item.vcIds.filter(isInFilter).forEach(id => withIds.add(id)));
      return {
        ...categoryData,
        overall: filterArray(categoryData.overall),
        byWidth: filterArray(categoryData.byWidth),
        byHeight: filterArray(categoryData.byHeight),
        totalWith: withIds.size,
        totalWithout: allFilteredIds.length - withIds.size,
      };
    }

    if (categoryData.type === 'imageMedia') {
      const filterArray = (arr) => (arr || []).map(item => {
        const filteredIds = item.vcIds.filter(isInFilter);
        return { ...item, vcIds: filteredIds, count: filteredIds.length };
      }).filter(item => item.count > 0);
      const allFilteredIds = Object.keys(categoryData.artMap).map(Number).filter(isInFilter);
      const withIds = new Set();
      (categoryData.byCount || []).forEach(item => item.vcIds.filter(isInFilter).forEach(id => withIds.add(id)));
      return {
        ...categoryData,
        byCategory: filterArray(categoryData.byCategory),
        byCount: filterArray(categoryData.byCount),
        totalWith: withIds.size,
        totalWithout: allFilteredIds.length - withIds.size,
      };
    }

    if (categoryData.items) {
      const newItems = categoryData.items.map(item => {
        const filteredIds = item.vcIds.filter(isInFilter);
        return { ...item, vcIds: filteredIds, count: filteredIds.length };
      }).filter(item => item.count > 0);
      const allFilteredIds = Object.keys(categoryData.artMap).map(Number).filter(isInFilter);
      const withIds = new Set();
      newItems.forEach(item => item.vcIds.forEach(id => withIds.add(id)));
      return {
        ...categoryData,
        items: newItems,
        totalWith: withIds.size,
        totalWithout: allFilteredIds.length - withIds.size,
        missingIds: allFilteredIds.filter(id => !withIds.has(id)),
      };
    }

    return categoryData;
  }, [categoryData, collectionFilter, acceptedOnly, artworkCollections, collectionAccepted]);

  // Load available tags once (including cover_vc_id)
  useEffect(() => {
    const loadAvailableTags = async () => {
      const { data, error } = await supabase
        .from('artwork_attributes')
        .select('id, title, type, cover_vc_id, order')
        .eq('approved', true)
        .order('order', { nullsFirst: false })
        .order('title');
      if (!error && data) {
        setAvailableTags(data);
        // Build cover image lookup from cover_vc_id
        const coverVcIds = [...new Set(data.filter(t => t.cover_vc_id).map(t => t.cover_vc_id))];
        if (coverVcIds.length > 0) {
          const { data: coverArts } = await supabase
            .from('vcs')
            .select('vc_id, cover_image, title')
            .in('vc_id', coverVcIds);
          if (coverArts) {
            const coverMap = {};
            coverArts.forEach(a => { coverMap[a.vc_id] = a; });
            const tagCovers = {};
            data.forEach(tag => {
              if (tag.cover_vc_id && coverMap[tag.cover_vc_id]) {
                tagCovers[tag.id] = coverMap[tag.cover_vc_id];
              }
            });
            setTagCoverImages(tagCovers);
          }
        }
      }
    };
    loadAvailableTags();
  }, []);

  // Open tag editor for an artwork
  const openTagEditor = useCallback(async (artwork) => {
    setTagEditorArtwork(artwork);
    setLoadingTagEditor(true);
    setReviewStatus(null);
    try {
      const { data, error } = await supabase
        .from('attribute_vc_mapping')
        .select('mapping_id, attribute_id, artwork_attributes!inner(id, title, type)')
        .eq('vc_id', artwork.vc_id);
      if (!error && data) {
        setArtworkTagMappings(
          data
            .map(d => ({ mappingId: d.mapping_id, attributeId: d.attribute_id, type: d.artwork_attributes?.type }))
        );
      }
      // Load review status
      if (artwork.tags_reviewed_by) {
        const { data: reviewer } = await supabase
          .from('users')
          .select('first_name, last_name')
          .eq('uuid', artwork.tags_reviewed_by)
          .single();
        setReviewStatus({
          reviewedBy: artwork.tags_reviewed_by,
          reviewedAt: artwork.tags_reviewed_at,
          reviewerName: reviewer ? `${reviewer.first_name || ''} ${reviewer.last_name || ''}`.trim() : 'Unknown',
        });
      }
    } catch (err) {
      console.error('Error loading artwork tags:', err);
    } finally {
      setLoadingTagEditor(false);
    }
  }, []);

  const closeTagEditor = useCallback(() => {
    setTagEditorArtwork(null);
    setArtworkTagMappings([]);
    setReviewStatus(null);
  }, []);

  // Mark artwork tags as reviewed by current curator
  const markAsReviewed = useCallback(async () => {
    if (!tagEditorArtwork || !currentUserId || markingReviewed) return;
    setMarkingReviewed(true);
    try {
      const now = new Date().toISOString();
      const { error } = await supabase
        .from('vcs')
        .update({ tags_reviewed_by: currentUserId, tags_reviewed_at: now })
        .eq('vc_id', tagEditorArtwork.vc_id);
      if (error) throw error;
      // Update local artwork data so the card badge updates
      tagEditorArtwork.tags_reviewed_by = currentUserId;
      tagEditorArtwork.tags_reviewed_at = now;
      // Get reviewer name for display
      const { data: reviewer } = await supabase
        .from('users')
        .select('first_name, last_name')
        .eq('uuid', currentUserId)
        .single();
      setReviewStatus({
        reviewedBy: currentUserId,
        reviewedAt: now,
        reviewerName: reviewer ? `${reviewer.first_name || ''} ${reviewer.last_name || ''}`.trim() : 'You',
      });
    } catch (err) {
      console.error('Error marking as reviewed:', err);
    } finally {
      setMarkingReviewed(false);
    }
  }, [tagEditorArtwork, currentUserId, markingReviewed]);

  // Add a new tag to artwork_attributes
  const addNewTag = useCallback(async (type) => {
    const title = newTagTitle.trim().toLowerCase();
    if (!title || savingNewTag) return;
    // Check for duplicates
    if (availableTags.some(t => t.type === type && t.title.toLowerCase() === title)) {
      setAddingTagType(null);
      setNewTagTitle('');
      return;
    }
    setSavingNewTag(true);
    try {
      const { data, error } = await supabase
        .from('artwork_attributes')
        .insert({ title, type, approved: true })
        .select('id, title, type, cover_vc_id')
        .single();
      if (error) throw error;
      setAvailableTags(prev => [...prev, data].sort((a, b) => a.title.localeCompare(b.title)));
      setAddingTagType(null);
      setNewTagTitle('');
    } catch (err) {
      console.error('Error adding new tag:', err);
    } finally {
      setSavingNewTag(false);
    }
  }, [newTagTitle, savingNewTag, availableTags]);

  // Start reordering a tag type
  const startReorder = useCallback((type) => {
    const tagsOfType = availableTags
      .filter(t => t.type === type)
      .sort((a, b) => (a.order ?? 9999) - (b.order ?? 9999));
    setReorderList(tagsOfType);
    setReorderingType(type);
  }, [availableTags]);

  // Move a tag up or down in the reorder list
  const moveTag = useCallback((index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= reorderList.length) return;
    const newList = [...reorderList];
    [newList[index], newList[newIndex]] = [newList[newIndex], newList[index]];
    setReorderList(newList);
  }, [reorderList]);

  // Handle drag start/over/drop for reordering
  const handleDragStart = useCallback((index) => {
    setDragIndex(index);
  }, []);

  const handleDragOver = useCallback((e, index) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === index) return;
    const newList = [...reorderList];
    const [moved] = newList.splice(dragIndex, 1);
    newList.splice(index, 0, moved);
    setReorderList(newList);
    setDragIndex(index);
  }, [dragIndex, reorderList]);

  const handleDragEnd = useCallback(() => {
    setDragIndex(null);
  }, []);

  // Save the new order to the database
  const saveOrder = useCallback(async () => {
    if (savingOrder) return;
    setSavingOrder(true);
    try {
      const updates = reorderList.map((tag, i) => ({
        id: tag.id,
        order: i + 1,
      }));
      for (const u of updates) {
        const { error } = await supabase
          .from('artwork_attributes')
          .update({ order: u.order })
          .eq('id', u.id);
        if (error) throw error;
      }
      // Update local availableTags state
      setAvailableTags(prev => {
        const orderMap = {};
        updates.forEach(u => { orderMap[u.id] = u.order; });
        return prev.map(t => orderMap[t.id] !== undefined ? { ...t, order: orderMap[t.id] } : t);
      });
      setReorderingType(null);
      setReorderList([]);
    } catch (err) {
      console.error('Error saving tag order:', err);
    } finally {
      setSavingOrder(false);
    }
  }, [reorderList, savingOrder]);

  // Cancel reordering
  const cancelReorder = useCallback(() => {
    setReorderingType(null);
    setReorderList([]);
    setDragIndex(null);
  }, []);

  // Set an artwork as the cover image for an attribute tag
  const setCoverImage = useCallback(async (attributeId, artwork) => {
    if (settingCover) return;
    setSettingCover(attributeId);
    try {
      const { error } = await supabase
        .from('artwork_attributes')
        .update({ cover_vc_id: artwork.vc_id })
        .eq('id', attributeId);
      if (error) throw error;
      // Update local state
      setTagCoverImages(prev => ({
        ...prev,
        [attributeId]: { vc_id: artwork.vc_id, cover_image: artwork.cover_image, title: artwork.title }
      }));
      setAvailableTags(prev => prev.map(t => t.id === attributeId ? { ...t, cover_vc_id: artwork.vc_id } : t));
    } catch (err) {
      console.error('Error setting cover image:', err);
    } finally {
      setSettingCover(null);
    }
  }, [settingCover]);

  // Toggle a tag on/off for the current artwork
  const toggleTag = useCallback(async (tag) => {
    if (!tagEditorArtwork || togglingTagId) return;
    const vcId = tagEditorArtwork.vc_id;
    const existing = artworkTagMappings.find(m => m.attributeId === tag.id);
    setTogglingTagId(tag.id);

    try {
      if (existing) {
        // Remove tag
        const { error } = await supabase
          .from('attribute_vc_mapping')
          .delete()
          .eq('mapping_id', existing.mappingId);
        if (error) throw error;
        setArtworkTagMappings(prev => prev.filter(m => m.mappingId !== existing.mappingId));
      } else {
        // Add tag — check limits for style/subject (max 3)
        if ((tag.type === 'style' || tag.type === 'subject')) {
          const count = artworkTagMappings.filter(m => m.type === tag.type).length;
          if (count >= 3) {
            setTogglingTagId(null);
            return;
          }
        }
        const { data, error } = await supabase
          .from('attribute_vc_mapping')
          .insert({ vc_id: vcId, attribute_id: tag.id })
          .select('mapping_id')
          .single();
        if (error) throw error;
        setArtworkTagMappings(prev => [...prev, { mappingId: data.mapping_id, attributeId: tag.id, type: tag.type }]);
      }
    } catch (err) {
      console.error('Error toggling tag:', err);
    } finally {
      setTogglingTagId(null);
    }
  }, [tagEditorArtwork, artworkTagMappings, togglingTagId]);

  // Close tag editor on Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && tagEditorArtwork) closeTagEditor();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [tagEditorArtwork, closeTagEditor]);

  // Initialize collection search text from URL param once collections load
  useEffect(() => {
    if (initialCollectionId && collections.length > 0 && !collectionSearch) {
      const match = collections.find(c => String(c.collection_id) === initialCollectionId);
      if (match) setCollectionSearch(match.title);
    }
  }, [collections, initialCollectionId]);

  // Close collection dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (collectionRef.current && !collectionRef.current.contains(e.target)) {
        setShowCollectionDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Group available tags by type for the editor
  const tagsByType = availableTags.reduce((acc, tag) => {
    if (!acc[tag.type]) acc[tag.type] = [];
    acc[tag.type].push(tag);
    return acc;
  }, {});
  const TAG_TYPE_ORDER = ['style', 'subject', 'medium', 'format', 'surface', 'color', 'tone'];
  const TAG_TYPE_LABELS = {
    style: { label: 'Style', hint: 'Choose up to 3' },
    subject: { label: 'Subject', hint: 'Choose up to 3' },
    medium: { label: 'Medium', hint: '' },
    format: { label: 'Format', hint: '' },
    surface: { label: 'Surface', hint: '' },
    color: { label: 'Color Palette', hint: '' },
    tone: { label: 'Color Tones', hint: '' },
  };

  // Find the active attribute when a tag sub-filter is selected
  const activeAttribute = useMemo(() => {
    if (activeCategory !== 'tags' || !activeSubFilter) return null;
    return availableTags.find(t => t.title === activeSubFilter) || null;
  }, [activeCategory, activeSubFilter, availableTags]);

  // === RENDER ===

  return (
    <div className="enrichment-drilldown">
      {/* Header */}
      <div className="ed-header">
        <button className="ed-back-btn" onClick={() => navigate('/admin/artworks')}>
          <ArrowLeft size={18} />
          Back to Artworks
        </button>
        <h1>
          <BarChart3 size={22} />
          Enrichment Explorer
        </h1>
      </div>

      {/* Category Tabs */}
      <div className="ed-category-tabs">
        {CATEGORIES.map(cat => {
          const Icon = cat.icon;
          return (
            <button
              key={cat.key}
              className={`ed-cat-tab ${activeCategory === cat.key ? 'active' : ''}`}
              onClick={() => setCategory(cat.key)}
              style={activeCategory === cat.key ? { borderColor: cat.color, color: cat.color } : {}}
            >
              <Icon size={14} />
              {cat.label}
            </button>
          );
        })}
      </div>

      <div className="ed-content">
        {/* Left: Breakdown Panel */}
        <div className="ed-breakdown-panel">
          <div className="ed-breakdown-header">
            <CategoryIcon size={18} color={categoryDef.color} />
            <h2>{categoryDef.label}</h2>
            {filteredCategoryData && (
              <span className="ed-breakdown-summary">
                {filteredCategoryData.totalWith || 0} have · {filteredCategoryData.totalWithout || 0} missing
                {filteredCategoryData.totalCurated > 0 && (
                  <> · <span className="ed-curated-count">{filteredCategoryData.totalCurated} curated</span></>
                )}
              </span>
            )}
          </div>

          {/* Has/Missing toggle */}
          {filteredCategoryData && (
            <div className="ed-toggle-bar">
              <button
                className={`ed-toggle-btn ${!showMissing && !activeSubFilter ? 'active' : ''}`}
                onClick={() => { setSubFilter(null); if (showMissing) toggleShowMissing(); }}
              >
                <CheckCircle2 size={13} />
                Has ({filteredCategoryData.totalWith || 0})
              </button>
              <button
                className={`ed-toggle-btn ${showMissing ? 'active missing' : ''}`}
                onClick={() => { setSubFilter(null); if (!showMissing) toggleShowMissing(); }}
              >
                <X size={13} />
                Missing ({filteredCategoryData.totalWithout || 0})
              </button>
            </div>
          )}

          {/* Breakdown list */}
          {loading ? (
            <div className="ed-loading">
              <RefreshCw size={20} className="spinning" />
              <span>Analyzing all artworks...</span>
            </div>
          ) : filteredCategoryData?.type === 'tags' ? (
            // Tags: grouped by type
            <div className="ed-tag-groups">
              {Object.entries(filteredCategoryData.breakdown || {})
                .sort(([a], [b]) => {
                  const order = ['style', 'subject', 'feeling', 'color', 'tone', 'medium'];
                  return order.indexOf(a) - order.indexOf(b);
                })
                .map(([type, tags]) => {
                  // Sort tags by the order field from availableTags
                  const sortedTags = [...tags].sort((a, b) => {
                    const attrA = availableTags.find(t => t.title === a.title && t.type === type);
                    const attrB = availableTags.find(t => t.title === b.title && t.type === type);
                    return (attrA?.order ?? 9999) - (attrB?.order ?? 9999);
                  });
                  return (
                  <div key={type} className="ed-tag-group">
                    <div className="ed-tag-type-header">
                      <h3 className="ed-tag-type">{type.charAt(0).toUpperCase() + type.slice(1)}</h3>
                      {reorderingType === type ? (
                        <div className="ed-reorder-actions">
                          <button className="ed-reorder-save" onClick={saveOrder} disabled={savingOrder}>
                            <Save size={12} />{savingOrder ? 'Saving...' : 'Save'}
                          </button>
                          <button className="ed-reorder-cancel" onClick={cancelReorder}>
                            <X size={12} />Cancel
                          </button>
                        </div>
                      ) : (
                        <button className="ed-reorder-btn" onClick={() => startReorder(type)} title="Reorder tags">
                          <ArrowUpDown size={12} />
                        </button>
                      )}
                    </div>
                    {reorderingType === type ? (
                      <div className="ed-tag-list ed-reorder-list">
                        {reorderList.map((tag, index) => (
                          <div
                            key={tag.id}
                            className={`ed-reorder-item ${dragIndex === index ? 'dragging' : ''}`}
                            draggable
                            onDragStart={() => handleDragStart(index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDragEnd={handleDragEnd}
                          >
                            <GripVertical size={14} className="ed-reorder-grip" />
                            <span className="ed-reorder-index">{index + 1}</span>
                            <span className="ed-tag-name">{tag.title}</span>
                            <div className="ed-reorder-arrows">
                              <button onClick={() => moveTag(index, -1)} disabled={index === 0}>
                                <ChevronUp size={14} />
                              </button>
                              <button onClick={() => moveTag(index, 1)} disabled={index === reorderList.length - 1}>
                                <ChevronDown size={14} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                    <div className="ed-tag-list">
                      {sortedTags.map(tag => {
                        const attr = availableTags.find(t => t.title === tag.title && t.type === type);
                        const coverInfo = attr ? tagCoverImages[attr.id] : null;
                        return (
                        <button
                          key={tag.title}
                          className={`ed-tag-item ${activeSubFilter === tag.title ? 'active' : ''} ${coverInfo ? 'has-cover' : ''}`}
                          onClick={() => setSubFilter(activeSubFilter === tag.title ? null : tag.title)}
                        >
                          {coverInfo ? (
                            <img
                              className="ed-tag-cover-thumb"
                              src={getDisplayImageUrl(coverInfo.cover_image)}
                              alt=""
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          ) : (
                            <span className="ed-tag-no-cover" title="No cover image set" />
                          )}
                          <span className="ed-tag-name">{tag.title}</span>
                          <span className="ed-tag-count">{tag.count}</span>
                        </button>
                        );
                      })}
                    </div>
                    )}
                  </div>
                );})}
            </div>
          ) : filteredCategoryData?.type === 'imageMedia' ? (
            <div className="ed-dimensions-panel">
              <div className="ed-dim-mode-tabs">
                {[{ key: 'byCategory', label: 'By Type' }, { key: 'byCount', label: 'By Count' }].map(m => (
                  <button
                    key={m.key}
                    className={`ed-dim-mode-btn ${imageViewMode === m.key ? 'active' : ''}`}
                    onClick={() => { setImageViewMode(m.key); setSubFilter(null); setVisibleCount(24); }}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
              <div className="ed-breakdown-list">
                {(imageViewMode === 'byCount' ? filteredCategoryData.byCount : filteredCategoryData.byCategory
                ).map(item => (
                  <button
                    key={item.title}
                    className={`ed-breakdown-item ${activeSubFilter === item.title ? 'active' : ''}`}
                    onClick={() => setSubFilter(activeSubFilter === item.title ? null : item.title)}
                  >
                    <span className="ed-item-name">{item.title}</span>
                    <span className="ed-item-count">{item.count} {imageViewMode === 'byCount' ? 'artworks' : 'images'}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : filteredCategoryData?.type === 'breakdown' ? (
            <div className="ed-breakdown-list">
              {(filteredCategoryData.items || []).map(item => (
                <button
                  key={item.title}
                  className={`ed-breakdown-item ${activeSubFilter === item.title ? 'active' : ''}`}
                  onClick={() => setSubFilter(activeSubFilter === item.title ? null : item.title)}
                >
                  <span className="ed-item-name">{item.title}</span>
                  <span className="ed-item-count">{item.count} artworks</span>
                </button>
              ))}
            </div>
          ) : filteredCategoryData?.type === 'dimensions' ? (
            <div className="ed-dimensions-panel">
              <div className="ed-dim-mode-tabs">
                {[{ key: 'overall', label: 'Overall Size' }, { key: 'width', label: 'Width' }, { key: 'height', label: 'Height' }].map(m => (
                  <button
                    key={m.key}
                    className={`ed-dim-mode-btn ${dimensionMode === m.key ? 'active' : ''}`}
                    onClick={() => { setDimensionMode(m.key); setSubFilter(null); setVisibleCount(24); }}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
              <div className="ed-breakdown-list">
                {(dimensionMode === 'width' ? filteredCategoryData.byWidth
                  : dimensionMode === 'height' ? filteredCategoryData.byHeight
                  : filteredCategoryData.overall
                ).map(item => (
                  <button
                    key={item.title}
                    className={`ed-breakdown-item ${activeSubFilter === item.title ? 'active' : ''}`}
                    onClick={() => setSubFilter(activeSubFilter === item.title ? null : item.title)}
                  >
                    <span className="ed-item-name">{item.title}</span>
                    <span className="ed-item-count">{item.count} artworks</span>
                  </button>
                ))}
              </div>
            </div>
          ) : filteredCategoryData?.type === 'simple' ? (
            <div className="ed-simple-summary">
              <div className="ed-simple-stat has">
                <CheckCircle2 size={18} />
                <strong>{filteredCategoryData.totalWith}</strong> artworks have {categoryDef.label.toLowerCase()}
              </div>
              <div className="ed-simple-stat missing">
                <X size={18} />
                <strong>{filteredCategoryData.totalWithout}</strong> artworks missing {categoryDef.label.toLowerCase()}
              </div>
            </div>
          ) : null}
        </div>

        {/* Right: Artwork Grid */}
        <div className="ed-artwork-panel">
          <div className="ed-artwork-header">
            <h3>
              {activeSubFilter
                ? `"${activeSubFilter}"`
                : showMissing
                ? `Missing ${categoryDef.label}`
                : `Has ${categoryDef.label}`
              }
              <span className="ed-artwork-count">{displayArtworks.length} artworks</span>
            </h3>
            <div className="ed-artwork-controls">
              <div className="ed-sort-control">
                <ArrowUpDown size={14} />
                <select
                  value={sortBy}
                  onChange={(e) => { setSortBy(e.target.value); setVisibleCount(24); }}
                  className="ed-sort-select"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="title-asc">Title A–Z</option>
                  <option value="title-desc">Title Z–A</option>
                  <option value="price-high">Price High→Low</option>
                  <option value="price-low">Price Low→High</option>
                  <option value="artist-asc">Artist A–Z</option>
                </select>
              </div>
              <div className="ed-artwork-search">
                <Search size={14} />
                <input
                  type="text"
                  placeholder="Filter by title or artist..."
                  value={artworkSearch}
                  onChange={(e) => { setArtworkSearch(e.target.value); setVisibleCount(24); }}
                />
                {artworkSearch && (
                  <button onClick={() => setArtworkSearch('')}><X size={14} /></button>
                )}
              </div>
              {collections.length > 0 && (
                <div className="ed-collection-filter" ref={collectionRef}>
                  <Search size={14} />
                  <input
                    type="text"
                    placeholder="Filter by collection..."
                    value={collectionSearch}
                    onChange={(e) => {
                      setCollectionSearch(e.target.value);
                      setShowCollectionDropdown(true);
                      if (!e.target.value) {
                        setCollectionFilter('');
                        setAcceptedOnly(false);
                        setVisibleCount(24);
                      }
                    }}
                    onFocus={() => setShowCollectionDropdown(true)}
                  />
                  {collectionSearch && (
                    <button className="ed-collection-clear" onClick={() => {
                      setCollectionSearch('');
                      setCollectionFilter('');
                      setAcceptedOnly(false);
                      setShowCollectionDropdown(false);
                      setVisibleCount(24);
                    }}><X size={14} /></button>
                  )}
                  {showCollectionDropdown && (
                    <div className="ed-collection-dropdown">
                      {collections
                        .filter(c => (c.title || '').toLowerCase().includes(collectionSearch.toLowerCase()))
                        .slice(0, 20)
                        .map(c => (
                          <button
                            key={c.collection_id}
                            className={`ed-collection-option ${collectionFilter === String(c.collection_id) ? 'active' : ''}`}
                            onClick={() => {
                              setCollectionFilter(String(c.collection_id));
                              setCollectionSearch(c.title);
                              setShowCollectionDropdown(false);
                              setVisibleCount(24);
                            }}
                          >
                            <span>{c.title}</span>
                            <span className="ed-collection-count">{c.artwork_count}</span>
                          </button>
                        ))
                      }
                      {collections.filter(c => (c.title || '').toLowerCase().includes(collectionSearch.toLowerCase())).length === 0 && (
                        <div className="ed-collection-empty">No collections found</div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {collectionFilter && (
                <div className="ed-accepted-toggle">
                  <button
                    className={`ed-accepted-btn ${!acceptedOnly ? 'active' : ''}`}
                    onClick={() => { setAcceptedOnly(false); setVisibleCount(24); }}
                  >All</button>
                  <button
                    className={`ed-accepted-btn ${acceptedOnly ? 'active' : ''}`}
                    onClick={() => { setAcceptedOnly(true); setVisibleCount(24); }}
                  >Accepted</button>
                </div>
              )}
            </div>
          </div>

          {loading ? (
            <div className="ed-loading-grid">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="ed-skeleton-card" />
              ))}
            </div>
          ) : visibleArtworks.length === 0 ? (
            <div className="ed-empty">
              <CategoryIcon size={32} color="#ccc" />
              <p>No artworks found</p>
            </div>
          ) : (
            <>
              <div className="ed-artwork-grid">
                {visibleArtworks.map(artwork => {
                  const isCoverForActiveTag = activeAttribute && tagCoverImages[activeAttribute.id]?.vc_id === artwork.vc_id;
                  return (
                  <div
                    key={artwork.vc_id}
                    className={`ed-artwork-card ${activeCategory === 'tags' ? 'taggable' : ''} ${tagEditorArtwork?.vc_id === artwork.vc_id ? 'tag-editing' : ''} ${isCoverForActiveTag ? 'is-tag-cover' : ''}`}
                    onClick={activeCategory === 'tags' ? () => openTagEditor(artwork) : undefined}
                  >
                    <div className="ed-card-image">
                      <img
                        src={getDisplayImageUrl(artwork.cover_image) || '/images/placeholder-artwork.jpg'}
                        alt={artwork.title}
                        onError={(e) => { e.target.src = '/images/placeholder-artwork.jpg'; }}
                      />
                      {isCoverForActiveTag && (
                        <div className="ed-card-cover-badge" title="Cover image for this tag">
                          <Star size={14} />
                          <span>Cover</span>
                        </div>
                      )}
                      {activeCategory === 'tags' && artwork.tags_reviewed_at && (
                        <div className="ed-card-reviewed-badge" title={`Reviewed ${new Date(artwork.tags_reviewed_at).toLocaleDateString()}`}>
                          <CheckCircle2 size={14} />
                        </div>
                      )}
                      {activeCategory === 'tags' ? (
                        <div className="ed-card-overlay tag-overlay">
                          <Tag size={16} />
                          <span>Edit Tags</span>
                        </div>
                      ) : (
                        <div className="ed-card-overlay">
                          <button
                            className="ed-card-view"
                            onClick={() => navigate(`/admin/artworks/${artwork.vc_id}/edit`)}
                            title="Edit artwork"
                          >
                            <Edit3 size={16} />
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="ed-card-info">
                      <h4>{artwork.title || 'Untitled'}</h4>
                      <p>{artwork.artist_name}</p>
                      <div className="ed-card-meta">
                        {artwork.price > 0 && <span className="ed-card-price">${artwork.price.toLocaleString()}</span>}
                        {(artwork.width || artwork.height) && (
                          <span className="ed-card-dims">
                            {artwork.width || '?'} × {artwork.height || '?'}"
                          </span>
                        )}
                      </div>
                      {activeAttribute && !isCoverForActiveTag && (
                        <button
                          className="ed-set-cover-btn"
                          onClick={(e) => { e.stopPropagation(); setCoverImage(activeAttribute.id, artwork); }}
                          disabled={settingCover === activeAttribute.id}
                          title={`Set as cover image for "${activeSubFilter}"`}
                        >
                          <Star size={12} />
                          {settingCover === activeAttribute.id ? 'Setting...' : 'Set as Cover'}
                        </button>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>

              {/* Infinite scroll sentinel */}
              {visibleCount < displayArtworks.length && (
                <div ref={loadMoreRef} className="ed-load-more-sentinel">
                  <span className="ed-loading-more">Loading more...</span>
                </div>
              )}
              {visibleCount >= displayArtworks.length && displayArtworks.length > 24 && (
                <div className="ed-all-loaded">All {displayArtworks.length} artworks shown</div>
              )}
            </>
          )}
        </div>
      </div>
      {/* Tag Editor Drawer */}
      {tagEditorArtwork && (
        <div className="ed-tag-drawer-backdrop" onClick={closeTagEditor}>
          <div
            className="ed-tag-drawer"
            ref={tagEditorRef}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ed-tag-drawer-header">
              <h3><Tag size={16} /> Edit Tags</h3>
              <button className="ed-tag-drawer-close" onClick={closeTagEditor}>
                <X size={18} />
              </button>
            </div>

            <div className="ed-tag-drawer-artwork">
              <img
                src={getDisplayImageUrl(tagEditorArtwork.cover_image) || '/images/placeholder-artwork.jpg'}
                alt={tagEditorArtwork.title}
                className="ed-tag-drawer-thumb"
                onError={(e) => { e.target.src = '/images/placeholder-artwork.jpg'; }}
                onClick={() => {
                  const fullUrl = tagEditorArtwork.cover_image?.includes('/upload/')
                    ? tagEditorArtwork.cover_image.replace('/upload/', '/upload/t_800/')
                    : tagEditorArtwork.cover_image;
                  setLightboxUrl(fullUrl);
                }}
                title="Click to view full size"
              />
              <div className="ed-tag-drawer-artwork-info">
                <h4>{tagEditorArtwork.title || 'Untitled'}</h4>
                <p>{tagEditorArtwork.artist_name}</p>
                {(tagEditorArtwork.width || tagEditorArtwork.height) && (
                  <span className="ed-tag-drawer-dims">
                    {tagEditorArtwork.width || '?'} × {tagEditorArtwork.height || '?'}"
                  </span>
                )}
                {tagEditorArtwork.description && (
                  <p className="ed-tag-drawer-description">{tagEditorArtwork.description}</p>
                )}
              </div>
            </div>

            {(tagEditorArtwork.more?.inspiration || tagEditorArtwork.more?.process) && (
              <div className="ed-tag-drawer-story">
                {tagEditorArtwork.more.inspiration && (
                  <div className="ed-tag-drawer-story-section">
                    <strong>Inspiration:</strong>
                    <p>{tagEditorArtwork.more.inspiration}</p>
                  </div>
                )}
                {tagEditorArtwork.more.process && (
                  <div className="ed-tag-drawer-story-section">
                    <strong>Process:</strong>
                    <p>{tagEditorArtwork.more.process}</p>
                  </div>
                )}
              </div>
            )}

            <div className="ed-tag-drawer-body">
              {loadingTagEditor ? (
                <div className="ed-loading">
                  <RefreshCw size={18} className="spinning" />
                  <span>Loading tags...</span>
                </div>
              ) : (
                TAG_TYPE_ORDER.filter(type => tagsByType[type]?.length > 0).map(type => {
                  const config = TAG_TYPE_LABELS[type] || { label: type, hint: '' };
                  const assignedCount = artworkTagMappings.filter(m => m.type === type).length;
                  const atLimit = (type === 'style' || type === 'subject') && assignedCount >= 3;
                  return (
                    <div key={type} className="ed-tag-drawer-section">
                      <div className="ed-tag-drawer-section-header">
                        <h4>{config.label}</h4>
                        {config.hint && <span className="ed-tag-drawer-hint">{config.hint}</span>}
                        <span className="ed-tag-drawer-count">{assignedCount}</span>
                        <button
                          className="ed-tag-add-btn"
                          title={`Add new ${config.label.toLowerCase()}`}
                          onClick={() => {
                            if (addingTagType === type) {
                              setAddingTagType(null);
                              setNewTagTitle('');
                            } else {
                              setAddingTagType(type);
                              setNewTagTitle('');
                            }
                          }}
                        >
                          <Plus size={13} />
                        </button>
                      </div>
                      {addingTagType === type && (
                        <div className="ed-tag-add-input">
                          <input
                            type="text"
                            placeholder={`New ${config.label.toLowerCase()}...`}
                            value={newTagTitle}
                            onChange={(e) => setNewTagTitle(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') addNewTag(type);
                              if (e.key === 'Escape') { setAddingTagType(null); setNewTagTitle(''); }
                            }}
                            autoFocus
                            disabled={savingNewTag}
                          />
                          <button
                            className="ed-tag-add-save"
                            onClick={() => addNewTag(type)}
                            disabled={!newTagTitle.trim() || savingNewTag}
                          >
                            {savingNewTag ? '...' : 'Add'}
                          </button>
                        </div>
                      )}
                      <div className="ed-tag-drawer-chips">
                        {tagsByType[type].map(tag => {
                          const isAssigned = artworkTagMappings.some(m => m.attributeId === tag.id);
                          const isToggling = togglingTagId === tag.id;
                          const isDisabled = !isAssigned && atLimit;
                          return (
                            <button
                              key={tag.id}
                              className={`ed-tag-chip ${isAssigned ? 'active' : ''} ${isDisabled ? 'disabled' : ''} ${isToggling ? 'toggling' : ''}`}
                              onClick={() => toggleTag(tag)}
                              disabled={isDisabled || isToggling}
                            >
                              {tag.title}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="ed-tag-drawer-footer">
              {reviewStatus ? (
                <span className="ed-tag-drawer-reviewed">
                  <CheckCircle2 size={14} />
                  Reviewed by {reviewStatus.reviewerName} on {new Date(reviewStatus.reviewedAt).toLocaleDateString()}
                </span>
              ) : (
                <span className="ed-tag-drawer-total">
                  {artworkTagMappings.length} tag{artworkTagMappings.length !== 1 ? 's' : ''} assigned
                </span>
              )}
              <div className="ed-tag-drawer-actions">
                {!reviewStatus && (
                  <button
                    className="ed-tag-drawer-review-btn"
                    onClick={markAsReviewed}
                    disabled={markingReviewed}
                  >
                    <CheckCircle2 size={14} />
                    {markingReviewed ? 'Saving...' : 'Mark as Reviewed'}
                  </button>
                )}
                <button className="ed-tag-drawer-done" onClick={closeTagEditor}>
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Image Lightbox */}
      {lightboxUrl && (
        <div className="ed-lightbox" onClick={() => setLightboxUrl(null)}>
          <button className="ed-lightbox-close" onClick={() => setLightboxUrl(null)}>
            <X size={24} />
          </button>
          <img src={lightboxUrl} alt="Full size" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
};

export default EnrichmentDrilldown;
